import { useCallback, useEffect, useState } from 'react';
import { WebCollaborationRoomStatusRoomStatusEnum } from '../../api/generated';
import { GlobalActions } from '../../model';
import appRoutes from '../../routing/app-routes';
import { WebCollaborationState, webCollaborationSliceActions } from '../../store/features/web-collaboration';
import { acceptWBAnswer, getWBStatus, postWBAnswer, postWBCandidate, postWBOffer } from '../../store/features/web-collaboration/web-collaboration-thunks';
import { useAppDispatch, useAppSelector } from '../redux';

export interface WebCollaborationLogic {
  status: WebCollaborationRoomStatusRoomStatusEnum;
  offer: string;
  answer: string;
  candidate: string;
  roomId: string;
  screenSharingActive: boolean;
  createOffer: (offer: string) => void;
  createAnswer: (answer: string) => void;
  createCandidate: (candidate: RTCIceCandidate) => void;
  acceptAnswer: () => void;
  closeCall: () => void;
  resetOffer: () => void;
  setScreenSharingActive: (value: boolean) => void;
}

let internalCandidates: string;

export const useWebCollaborationLogic = (actions: GlobalActions): WebCollaborationLogic => {
  const dispatch = useAppDispatch();
  const {navigate} = actions;
  const { quitMifid } = useAppSelector((state) => state.features.mifidSurvey);
  const [localInterval, setLocalInterval] = useState<NodeJS.Timer>();

  const token: boolean = useAppSelector((state) => state.data?.user?.token);
  const {offer, answer, status, roomId, candidate, exit, screenSharingActive }: WebCollaborationState = useAppSelector((state) => state.features?.webCollaboration);





  const setScreenSharingActive = useCallback((value: boolean) => {
      dispatch(webCollaborationSliceActions.setScreenSharingActive(value));
  }, [dispatch]);

  const createOffer = useCallback((offerLoc: string) => {
      dispatch(postWBOffer({offer: offerLoc}));
  }, [dispatch]);

  const createAnswer = useCallback((answerLoc: string) => {
      dispatch(postWBAnswer({answer: answerLoc}));
  }, [dispatch]);
  const createCandidate = useCallback((iceCandidate: RTCIceCandidate) => {
    let candidates = [];
    if (internalCandidates) {
      candidates = JSON.parse(internalCandidates);
    }

    candidates.push(iceCandidate);
    internalCandidates = JSON.stringify(candidates);

    dispatch(postWBCandidate({iceCandidate: internalCandidates}));
  }, [dispatch]);

  const acceptAnswer = useCallback(() => {
      dispatch(acceptWBAnswer());
  }, [dispatch]);

  const closeCall = useCallback(() => {
    dispatch(webCollaborationSliceActions.exit());
    localInterval && clearInterval(localInterval);
    internalCandidates = undefined;
  }, []);

  const resetOffer = useCallback(() => {
    dispatch(webCollaborationSliceActions.resetOffer());
  }, [dispatch]);

  useEffect(() => {
    if (!localInterval && roomId && token) {
      const interval = setInterval(() => {
        dispatch(getWBStatus());
      }, 2000);
      setLocalInterval(interval);
    }
  }, [localInterval, roomId, token]);

  useEffect(() => {
    return () => {
      localInterval && clearInterval(localInterval);
    }
  }, [localInterval]);


  useEffect(() => {
    return () => internalCandidates = undefined;
  }, []);

  useEffect(() => {
    if (exit) {
      dispatch(webCollaborationSliceActions.reset());
      localInterval && clearInterval(localInterval);
      quitMifid ? quitMifid() : navigate(appRoutes.Dashboard, { replace: true });
    }
  }, [exit, localInterval]);

  return {
    offer,
    answer,
    status,
    roomId,
    candidate,
    screenSharingActive,
    createOffer,
    createAnswer,
    acceptAnswer,
    createCandidate,
    closeCall,
    resetOffer,
    setScreenSharingActive
  };
};
